import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wallet, Plus, Trash2, Loader2, Clock } from "lucide-react";
import { fetchPaymentAccounts, addPaymentAccount, deletePaymentAccount } from "@/lib/api";

const PROVIDER_COLORS = {
  "Wave": "bg-sky-100 text-sky-700",
  "Orange Money": "bg-orange-100 text-orange-700",
  "MTN MoMo": "bg-yellow-100 text-yellow-800",
  "Moov Money": "bg-blue-100 text-blue-700",
  "CinetPay": "bg-emerald-100 text-emerald-700",
};

export const PaymentAccountsSection = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ provider: "Wave", account_number: "", account_name: "" });
  const [busy, setBusy] = useState(false);

  const load = () => fetchPaymentAccounts().then(setData).catch(() => setData({ accounts: [], providers: [] }));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.account_number || !form.account_name) return toast.error("Numero et nom obligatoires");
    setBusy(true);
    try {
      await addPaymentAccount(form);
      toast.success("Compte ajoute");
      setShowForm(false);
      setForm({ provider: "Wave", account_number: "", account_name: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'ajout");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try { await deletePaymentAccount(id); load(); }
    catch { toast.error("Erreur"); }
  };

  if (!data) return null;

  return (
    <section className="glass-card p-7 max-w-2xl" data-testid="payment-accounts-section">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 text-white grid place-items-center shrink-0">
            <Wallet size={20} />
          </div>
          <div>
            <div className="font-bold text-lg">Mes comptes de paiement</div>
            <div className="text-xs text-[var(--ink-soft)]">Wave, Orange Money, MTN MoMo, Moov Money, CinetPay — pour recevoir vos fonds.</div>
          </div>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-secondary !px-3 inline-flex items-center gap-1 text-sm" data-testid="add-payment-account-btn">
          <Plus size={14} /> Ajouter
        </button>
      </div>

      {showForm && (
        <div className="mt-4 p-4 rounded-xl bg-orange-50/60 space-y-3" data-testid="payment-account-form">
          <select
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            className="w-full rounded-lg border border-[var(--line)] px-3 py-2 text-sm bg-white"
            data-testid="pa-provider"
          >
            {(data.providers || []).map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <input
            value={form.account_number}
            onChange={(e) => setForm({ ...form, account_number: e.target.value })}
            placeholder="Numero (ex: +225 07 99 22 02 42)"
            data-testid="pa-number"
          />
          <input
            value={form.account_name}
            onChange={(e) => setForm({ ...form, account_name: e.target.value })}
            placeholder="Nom du titulaire"
            data-testid="pa-name"
          />
          <button onClick={save} disabled={busy} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="pa-save">
            {busy ? <Loader2 size={14} className="animate-spin" /> : "Enregistrer le compte"}
          </button>
        </div>
      )}

      <div className="mt-4 space-y-2">
        {(data.accounts || []).length === 0 ? (
          <p className="text-sm text-[var(--ink-soft)]">Aucun compte enregistre.</p>
        ) : (
          data.accounts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-3 rounded-xl border border-[var(--line)]" data-testid={`payment-account-${a.id}`}>
              <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${PROVIDER_COLORS[a.provider] || "bg-gray-100 text-gray-700"}`}>
                {a.provider}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{a.account_number}</div>
                <div className="text-xs text-[var(--ink-soft)] truncate">{a.account_name}</div>
              </div>
              <button onClick={() => remove(a.id)} className="text-red-400 hover:text-red-600" data-testid={`pa-delete-${a.id}`}>
                <Trash2 size={15} />
              </button>
            </div>
          ))
        )}
      </div>

      <p className="text-xs text-[var(--ink-soft)] mt-4 inline-flex items-center gap-1.5" data-testid="transfers-pending-note">
        <Clock size={12} /> Les transferts de fonds vers ces comptes seront actives des la mise en service des paiements en production.
      </p>
    </section>
  );
};
